import * as React from "react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui";
import { CountryCodeSelector, CountryCode, COUNTRY_CODES } from "./CountryCodeSelector";

export interface MobileNumberInputProps {
  value: string;
  onChange: (value: string) => void;
  countryCode: CountryCode;
  onCountryChange: (country: CountryCode) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  className?: string;
  autoFocus?: boolean;
}

export const MobileNumberInput: React.FC<MobileNumberInputProps> = ({
  value,
  onChange,
  countryCode,
  onCountryChange,
  placeholder = "Enter mobile number",
  disabled = false,
  error,
  className,
  autoFocus = false,
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Format phone number as user types (remove non-digits)
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, "");
    onChange(digits);
  };

  // Format display value with spaces for readability
  const formatDisplayValue = (num: string): string => {
    if (!num) return "";
    // Format: XXX XXX XXXX or similar based on length
    if (num.length <= 3) return num;
    if (num.length <= 6) return `${num.slice(0, 3)} ${num.slice(3)}`;
    return `${num.slice(0, 3)} ${num.slice(3, 6)} ${num.slice(6, 10)}`;
  };

  return (
    <div className={cn("space-y-2", className)}>
      <div className="flex gap-2">
        <CountryCodeSelector
          value={countryCode}
          onChange={onCountryChange}
          disabled={disabled}
        />
        <Input
          ref={inputRef}
          type="tel"
          inputMode="numeric"
          value={formatDisplayValue(value)}
          onChange={handleChange}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus={autoFocus}
          autoComplete="tel"
          className={cn(
            "flex-1 h-14 text-lg font-medium tracking-wide",
            "placeholder:text-muted-foreground/60",
            error && "border-destructive focus-visible:ring-destructive"
          )}
        />
      </div>
      {error && (
        <p className="text-sm text-destructive animate-in fade-in slide-in-from-top-1">
          {error}
        </p>
      )}
    </div>
  );
};

// Helper to get full phone number with country code
export const getFullPhoneNumber = (countryCode: CountryCode, number: string): string => {
  return `${countryCode.dialCode}${number}`;
};

// Helper to detect country from phone number
export const detectCountryFromNumber = (phoneNumber: string): CountryCode | undefined => {
  const cleaned = phoneNumber.replace(/\D/g, "");
  return COUNTRY_CODES.find((c) => cleaned.startsWith(c.dialCode.replace("+", "")));
};

export default MobileNumberInput;
